import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, PLATFORM_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PersistentStorage } from './services/global/storage/persistent-storage.service';
import { createPersistentStorage, createSessionStorage } from './services/global/storage/storage-factory';
import { SessionStorage } from './services/global/storage/session-storage.service';
import { WizardComponent } from './components/wizard/wizard.component';
import { ConfigurationGuard } from './services/impl/configuration-gurad.service';
import { WizardStepComponent } from './components/wizard-step/wizard-step.component';
import { MainComponent } from './components/main/main.component';
import { ErrorNotificationWebComponent } from '../shared/sections/notifications/error-notification/error-notification-web.component';
import { CalculationStorageService } from '../shared/services/calculation-storage.service';
import { CalculationService } from './services/impl/calculation.service';
import { InsurerDataFormStateService } from './services/impl/section-services/insurer-data/insurer-data-form-state.service';
import { AgreementsStateService } from './services/impl/section-services/agreements/agreements-state.service';
import { WizardService } from './services/wizard.service';
import { NavigateManagerService } from './services/navigate-manager.service';
import { EventBusService } from './services/impl/event-bus.service';
import { OfferService } from './services/impl/offer.service';
import { SessionService } from './services/impl/session/session.service';
import { WebSocketService } from './services/impl/websocket/web-socket.service';
import { WebSocketEventRoutingService } from './services/impl/websocket/web-socket-event-routing.service';
import { OfferWebSocketHandlerService } from './services/impl/websocket/offer-web-socket-handler.service';
import { StepValidationStorageService } from '../shared/services/step-validation-storage.service';
import { ResourceService } from '../shared/services/resource.service';
import { OrderPolicyService } from './services/impl/order-policy.service';
import { PaymentService } from './services/impl/payment.service';
import { PaymentWebSocketHandlerService } from './services/impl/websocket/payment-web-socket-handler.service';
import { NotificationService } from './services/impl/notification.service';
import { NotificationWebSocketHandlerService } from './services/impl/websocket/notification-web-socket-handler.service';
import { SelectedOfferService } from './services/impl/section-services/selected-offer/selected-offer.service';
import { ExternalScriptService } from './services/impl/configuration/external-script.service';
import { NotificationStorageService } from '../shared/services/notification-storage.service';
import { PaymentMethodsService } from './services/impl/section-services/method-payments/payment-methods.service';
import { ScrollHelper } from '../shared/services/scroll-helper';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { ConnectionErrorNotificationWebComponent } from '../shared/sections/notifications/connection-error-notification/connection-error-notification-web.component';
import { SessionErrorNotificationWebComponent } from '../shared/sections/notifications/session-error-notification/session-error-notification-web.component';
import { ParamsResolverGuard } from './services/impl/params-resolver-guard.service';
import { InsuranceCompaniesAdditionalValidationService } from './services/insurance-companies-additional-validation.service';
import { OfferSectionStateService } from './services/impl/section-services/offers/offer-section-state.service';
import { InsurerContactDataService } from './services/impl/section-services/insurer-contact-data/insurer-contact-data.service';
import { AdditionalDataService } from './services/impl/section-services/additional-data/additional-data.service';
import { CalculationCacheService } from './services/impl/calculation-cache.service';
import { NotificationReadonlyNavComponent } from './components/notification-readonly-nav/notification-readonly-nav.component';
import { NarrowHeaderComponent } from './components/narrow-header/narrow-header.component';
import { HeaderComponent } from './components/next/header/header.component';
import { NavComponent } from './components/next/nav/nav.component';
import { BannerComponent } from './components/next/banner/banner.component';
import {NotificationWrapperComponent} from '../shared/components/notification-wrapper/notification-wrapper.component';

export interface CoreModuleConfig {
}


export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?nocache=${(new Date()).getTime()}`);
}

@NgModule({
  imports: [
    FormsModule,
    CommonModule,
    RouterModule.forRoot([{
      path: '',
      children: [
        {
          path: '',
          component: WizardComponent,
          canActivate: [ParamsResolverGuard, ConfigurationGuard],
          children: [
            {
              path: 'notifications',
              children: [
                {
                  path: 'success',
                  component: NotificationWrapperComponent,
                },
                {
                  path: 'cancel',
                  component: NotificationWrapperComponent,
                },
                {
                  path: 'accept',
                  component: NotificationWrapperComponent,
                },
                {
                  path: 'authorization',
                  component: NotificationWrapperComponent,
                },
              ]
            },
            {
              path: 'errors',
              component: NotificationWrapperComponent,
            }
          ]
        }
      ]
    }]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    ReactiveFormsModule,
    DeviceDetectorModule.forRoot(),
    SharedModule.forRoot()
  ],
  declarations: [
    WizardComponent,
    WizardStepComponent,
    MainComponent,
    ErrorNotificationWebComponent,
    ConnectionErrorNotificationWebComponent,
    SessionErrorNotificationWebComponent,
    NotificationReadonlyNavComponent,
    NarrowHeaderComponent,
    HeaderComponent,
    NavComponent,
    BannerComponent,
  ],
  providers: [
    {
      provide: PersistentStorage,
      useFactory: createPersistentStorage,
      deps: [Router, PLATFORM_ID]
    },
    {
      provide: SessionStorage,
      useFactory: createSessionStorage,
      deps: [Router, PLATFORM_ID]
    },
    InsuranceCompaniesAdditionalValidationService,
    ConfigurationGuard,
    CalculationStorageService,
    CalculationService,
    InsurerDataFormStateService,
    InsurerContactDataService,
    AdditionalDataService,
    AgreementsStateService,
    WizardService,
    NavigateManagerService,
    EventBusService,
    OfferService,
    OfferSectionStateService,
    SessionService,
    WebSocketService,
    WebSocketEventRoutingService,
    OfferWebSocketHandlerService,
    StepValidationStorageService,
    ResourceService,
    OrderPolicyService,
    PaymentService,
    PaymentWebSocketHandlerService,
    NotificationService,
    NotificationWebSocketHandlerService,
    SelectedOfferService,
    ExternalScriptService,
    NotificationStorageService,
    PaymentMethodsService,
    ScrollHelper,
    CalculationCacheService
  ],
  exports: [SharedModule, TranslateModule,
    MainComponent],
  entryComponents: [
    WizardStepComponent,
    ErrorNotificationWebComponent,
    ConnectionErrorNotificationWebComponent,
    SessionErrorNotificationWebComponent
  ]
})
export class CoreModule {
  static forRoot(config: CoreModuleConfig = {}): ModuleWithProviders {
    return {
      ngModule: CoreModule

    }
  }
}
